import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from "react-router-dom";

import i18n from 'utils/I18n';

import CustomAlert from '../base/CustomAlert';
import QuantitySelector from './QuantitySelector';
import TimeSlotPicker from './SlotPicker';
import OtherInfoSection from './OtherInfoSection';
import LoadMoreSpinner from "component/common/LoadMoreSpinner";
import CustomButton from 'component/base/CustomButton';
import { COUPON_CONFIRM_BUTTON_STATUS } from 'models/BuyCoupon';

import { ssoAPIHelper } from "services/SSOAPIHelper";
import { createAction, enLocaleNumberFormatter } from 'utils';
import { isNotSimplifiedNoWebapp } from "utils/UIConfigUtils";
import { openLoginPage } from "utils/SSOUtil"
import { detectIsMobileByScreenWidth } from 'utils/ScreenUtils';
import { WindowMessageType } from 'utils/CustomEnums';
import { ReactComponent as LeftArrow } from 'assets/back-btn.svg';
import './TicketInfoSection.scss';
import { useCallback } from 'react';

const BUY_TICKET_STEPS = {
  STEP_INPUT_INFO: "STEP_INPUT_INFO",
  STEP_CONFIRM: "STEP_CONFIRM",
};

const mapStateToProps = ({ buyCoupon, campaignDetail, users }) => ({
  confirmButtonStatus: buyCoupon.confirmButtonStatus,
  couponIsUnavaiable: buyCoupon.couponIsUnavaiable,
  errorMessage: buyCoupon.errorMessage,
  ticketCampaignModalLoading: campaignDetail.ticketCampaignModalLoading,
  ticketCampaignAdditionalData: campaignDetail.ticketCampaignAdditionalData,
  language: users.language,
  isLogin: users.isLogin,
  ignoreLogin: users.eventRegistrationData.ignoreLogin,
  availablePoints: users.availablePoints,
  eventRegistrationUserInfo: users.eventRegistrationUserInfo,
});

const TicketInfoSection = ({
  isEventActive = true,
  detailData, // ticket set data
  language,
  isLogin,
  confirmButtonStatus,
  couponIsUnavaiable,
  ticketCampaignModalLoading,
  ticketCampaignAdditionalData,
  eventRegistrationUserInfo,
  errorMessage,
  ignoreLogin, // customer do otp in sso and return, should skip login 
  availablePoints,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const noLoginInfo = !isLogin && !ignoreLogin

  const buyTicketData = detailData || {};
  const timeSlots = ticketCampaignAdditionalData?.tickets || [];

  const campaignID = buyTicketData?.campaignID;
  const enableOtp = buyTicketData?.enableOtp;
  const isTicketRequirePoint = buyTicketData?.requiredPoints > 0;

  const [step, setStep] = useState(BUY_TICKET_STEPS.STEP_INPUT_INFO);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState(null);
  const selectedTimeSlotStock = useMemo(() => {
    if (!selectedTimeSlotId) {
      return null;
    };
    let stock = null;
    // eslint-disable-next-line no-unused-expressions
    timeSlots?.forEach(item => {
      if (item.id === selectedTimeSlotId) {
        stock = item.stockCount;
      };
    });
    return stock;
  }, [timeSlots, selectedTimeSlotId]);

  const [count, setCount] = useState(buyTicketData.limit === 0 ? 0 : 1);
  const [btnMinusDisable, setBtnMinusDisable] = useState(true);
  const [btnAddDisable, setBtnAddDisable] = useState(
    buyTicketData.limit === 0 ||
    buyTicketData.limit === 1 ||
    buyTicketData.leftStock <= 1
  );

  const [questionData, setQuestionData] = useState([]);

  const [confirmButtonActive, setConfirmButtonActive] = useState(true);

  const [showAlert, setShowAlert] = useState(false);

  const [displaySsoIframe, setDisplaySsoIframe] = useState(false);
  const [ssoIframeHeight, setSsoIframeHeight] = useState(400);

  const isConfirmStep = step === BUY_TICKET_STEPS.STEP_CONFIRM;
  const totalRequiredPoints = useMemo(() => {
    if (!isTicketRequirePoint || !count) {
      return 0
    }
    return buyTicketData.requiredPoints * count;
  }, [isTicketRequirePoint, buyTicketData.requiredPoints, count]);

  const isQuestionNotFilled = useMemo(() =>
    questionData?.some(item => (
      !item.optional
      && !item.defaultValue
      // eslint-disable-next-line eqeqeq
      && !(item.defaultValue == 0)
    )
    ), [questionData])
  const pointsBalance = availablePoints - totalRequiredPoints;

  const nextButtonActive = useMemo(() => {
    if (noLoginInfo) {
      return true
    };
    // eslint-disable-next-line no-unused-expressions
    return !!selectedTimeSlotId && !!count && !isQuestionNotFilled && pointsBalance >= 0;
  }, [
    noLoginInfo,
    selectedTimeSlotId,
    count,
    isQuestionNotFilled,
    pointsBalance,
  ])
  const isButtonDisabled = !isEventActive || (isConfirmStep ? !confirmButtonActive : !nextButtonActive)

  const ssoLoginUrl = useMemo(() => {
    return ssoAPIHelper.getLoginWebUri({
      locale: language || "en",
      redirectTo: `/campaign/${campaignID}`,
      isEventRegister: true,
      eventRequireCompleteRegistration: isTicketRequirePoint,
      enableOtp: isTicketRequirePoint || enableOtp,
      isInIframe: true,
    })
  }, [campaignID, enableOtp, language, isTicketRequirePoint])

  const hideAlert = () => {
    setShowAlert(false);
  };

  const goToMyTicketOrRegisterResult = () => {
    // not login; login nowebapp webpage
    if (!isLogin || !isNotSimplifiedNoWebapp()) {
      history.push(`/campaign/${campaignID}/register_success`, {
        uid: eventRegistrationUserInfo.ssoUid,
        memberShipID: eventRegistrationUserInfo.membershipId,
      });
      return;
    }

    dispatch({
      type: 'myCoupon/changeTab',
      payload: { index: couponIsUnavaiable ? 1 : 0, isTicket: true }
    });
    history.push('/user/myTicket');
  }

  const handleReceivedMessage = useCallback((event) => {
    const { data } = event;
    const token = data?.token;

    switch (data?.type) {
      case WindowMessageType.RESIZE:
        data?.value && setSsoIframeHeight(data?.value);
        break;
      case WindowMessageType.USERS_LOGIN_SUCCESS:
        if (token) {
          dispatch(createAction("users/login")({
            code: token,
            successAction: () => {
              setDisplaySsoIframe(false)
            },
            failAction: () => {
              setDisplaySsoIframe(false)
            }
          }))
        }
        break;
      case WindowMessageType.CLOSE_SSO_IFRAME:
        setDisplaySsoIframe(false)
        break;
      case WindowMessageType.EVENT_REGISTER_SUCCESS:
        if (token) {
          dispatch(createAction("users/login")({
            code: token,
            successAction: () => {
              setDisplaySsoIframe(false);
              dispatch(createAction("navBars/updateState")({
                toastShowing: {
                  value: true,
                  content: i18n.t("signed_up_successfully"),
                  showTickIcon: true,
                },
              },
              ));
            },
            failAction: () => {
              setDisplaySsoIframe(false);
            }
          }))
          return;
        }
        setDisplaySsoIframe(false);
        const customerData = data?.data || {};
        dispatch(
          createAction("users/updateState")({
            eventRegistrationData: {
              verifyCredential: customerData?.verifyCredential || "",
              countryCode: customerData?.countryCode || "",
              phoneNumber: customerData?.mobile || "",
              email: customerData?.email || "",
              ignoreLogin: true,
            }
          })
        );
        break;
      default:
        break;
    }
  }, [dispatch])

  useEffect(() => {
    window.addEventListener("message", handleReceivedMessage)
    return () => {
      window.removeEventListener("message", handleReceivedMessage)
    }
  }, [handleReceivedMessage])

  useEffect(() => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS &&
      (!isLogin || !isNotSimplifiedNoWebapp())
      // nowebapp also go to qrcode
    ) {
      goToMyTicketOrRegisterResult()
      return;
    };

    if (
      confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
      || confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.FAIL
    ) {
      setShowAlert(true);
    }
  }, [confirmButtonStatus])

  useEffect(() => {
    dispatch({ type: 'buyCoupon/resetCouponModal' });
    if (!ignoreLogin) {
      dispatch({
        type: 'users/updateState',
        payload: {
          eventRegistrationData: {},
          eventRegistrationUserInfo: {},
        }
      })
    };

    return () => {
      dispatch({ type: 'buyCoupon/resetCouponModal' });
      dispatch({
        type: 'campaignDetail/updateState',
        payload: {
          ticketCampaignAdditionalData: {},
        }
      })
      dispatch({
        type: 'users/updateState',
        payload: {
          eventRegistrationData: {},
        }
      })
    }
  }, []);

  useEffect(() => {
    if (!campaignID) {
      return;
    }
    if (noLoginInfo) {
      return;
    };

    setStep(BUY_TICKET_STEPS.STEP_INPUT_INFO);
    dispatch(
      createAction("campaignDetail/getTicketCampaignAdditionalData")({
        campaignID: campaignID,
      })
    );
  }, [noLoginInfo, language, campaignID, dispatch])

  useEffect(() => {
    if (!selectedTimeSlotStock) {
      return;
    };
    let limit = selectedTimeSlotStock;
    if (buyTicketData.limit || buyTicketData.limit === 0) {
      limit = Math.min(selectedTimeSlotStock, buyTicketData.limit);
    };

    if (count >= limit) {
      setCount(limit);
      setBtnMinusDisable(limit <= 1);
      setBtnAddDisable(true);
    } else {
      setBtnMinusDisable(count <= 1);
      setBtnAddDisable(false);
    }
  }, [selectedTimeSlotStock]);

  useEffect(() => {
    setQuestionData(ticketCampaignAdditionalData.otherInfo || []);
  }, [ticketCampaignAdditionalData.otherInfo]);



  const updateCount = (newCount) => {
    const addDisable = [
      buyTicketData.limit,
      buyTicketData.leftStock,
      selectedTimeSlotStock,
    ].some(limit => (limit || limit === 0) && newCount >= limit)

    setCount(newCount);
    setBtnMinusDisable(newCount <= 1);
    setBtnAddDisable(addDisable);
  };

  const countMinus = () => {
    if (count > 1) {
      const newCount = count - 1;
      updateCount(newCount);
    }
  };

  const countAdd = () => {
    const newCount = count + 1;
    updateCount(newCount);
  };

  const onTimeSlotSelect = (timeSlotId) => {
    setSelectedTimeSlotId((prevId) => {
      if (timeSlotId === prevId) {
        return null;
      }
      return timeSlotId;
    });
  };

  const backAction = () => {
    setStep(BUY_TICKET_STEPS.STEP_INPUT_INFO);
  };

  const nextAction = () => {
    if (noLoginInfo) {
      if (detectIsMobileByScreenWidth()) {
        openLoginPage({
          locale: language || "en",
          redirectTo: `/campaign/${campaignID}`,
          isEventRegister: true,
          enableOtp: enableOtp,
        })

      }
      setDisplaySsoIframe(true);
      return;

    }

    setStep(BUY_TICKET_STEPS.STEP_CONFIRM);
  };

  const onConfirmButtonPress = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      goToMyTicketOrRegisterResult();
      return;
    };

    setConfirmButtonActive(false);
    dispatch(
      createAction("campaignDetail/participateTicketCampaign")({
        campaignID: campaignID,
        selectedTimeSlotId,
        count,
        questionData,
        afterAction: () => {
          setConfirmButtonActive(true);
        }
      })
    );
  };

  const buttonText = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      return i18n.t('myTicket');
    };
    if (isConfirmStep) {
      return i18n.t('coupon:Confirm');
    };
    if (isTicketRequirePoint) {
      return i18n.t('campaignDetail:buyTicket')
    }
    return i18n.t('getTicket');
  };

  const alertLeftBtnText = confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
    ? i18n.t('campaign_ok')
    : "";

  const alertLeftBtnAction = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      hideAlert();
      history.push(`/campaign/${campaignID}`);
    }
  };

  const alertRightBtn = confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS
    ? i18n.t('myTicket')
    : i18n.t('campaign_ok');

  const alertRightBtnAction = () => {
    if (confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS) {
      goToMyTicketOrRegisterResult();
    } else {
      dispatch({ type: 'buyCoupon/resetCouponModal' });
      hideAlert();
    }
  };

  if (ticketCampaignModalLoading) {
    return (
      <div className="ticket_info_section_loading">
        <LoadMoreSpinner />
      </div>
    )
  }

  const ticketSelectorSection = (
    <div className={`ticket_info_section_container ${noLoginInfo ? "ticket_info_section_container_none" : ""}`}>
      <TimeSlotPicker
        language={language}
        isConfirm={isConfirmStep}
        timeSlots={timeSlots}
        onTimeSlotSelect={onTimeSlotSelect}
        selectedTimeSlotId={selectedTimeSlotId}
      />

      <QuantitySelector
        language={language}
        isConfirm={isConfirmStep}
        count={count}
        btnMinusDisable={btnMinusDisable}
        btnAddDisable={btnAddDisable}
        countMinus={countMinus}
        countAdd={countAdd}
        isRequiredPoints={isTicketRequirePoint}
        requiredPoints={buyTicketData.requiredPoints}
      />

      <OtherInfoSection
        language={language}
        showOtherInfo={buyTicketData.showOtherInfo}
        questionData={questionData}
        setQuestionData={setQuestionData}
        isConfirm={isConfirmStep}
      />
    </div>
  )


  return (
    <>
      {
        isConfirmStep && confirmButtonStatus !== COUPON_CONFIRM_BUTTON_STATUS.SUCCESS && <div
          className='tick_campaign_back_button_container'
          onClick={backAction}
        >
          <LeftArrow className="ticket_campaign_back_button" />
        </div>
      }
      {
        displaySsoIframe && noLoginInfo ?
          <iframe
            title='event-register'
            id="ssoLoginIframe"
            src={ssoLoginUrl}
            style={{
              border: 'none',
              height: ssoIframeHeight || 400,
            }}
          /> :
          <>
            {ticketSelectorSection}
            <div className={`ticket_info_button_container ${noLoginInfo ? "ticket_info_button_container_none" : ""}`}>
              {isTicketRequirePoint && noLoginInfo ? <div>
              <div className="headline color-p1">
                  {i18n.t('campaign_required_points')}
                </div>
                <div className="title-medium-2 color-p2">
                  {`${enLocaleNumberFormatter(buyTicketData?.requiredPoints)} ${i18n.t('points')}`}
                </div>
              </div> : null}
              <CustomButton
                className="ticket_info_button"
                disabled={isButtonDisabled}
                text={buttonText()}
                btnClicked={isConfirmStep ? onConfirmButtonPress : nextAction}
              />
            </div>
          </>
      }
      <CustomAlert
        show={showAlert}
        onHide={hideAlert}
        title={confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.SUCCESS ? i18n.t("successfull_redeemed") : null}
        message={
          confirmButtonStatus === COUPON_CONFIRM_BUTTON_STATUS.FAIL
            ? errorMessage || ''
            : ''
        }
        leftbtn={alertLeftBtnText}
        leftAction={alertLeftBtnAction}
        rightbtn={alertRightBtn}
        rightAction={alertRightBtnAction}
        isLoading={false}
      ></CustomAlert>
    </>
  );
}


export default connect(mapStateToProps)(TicketInfoSection);
